(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
  const labels = JSON.parse(document.getElementById('BulkCartStrings')?.textContent || '{}');
  const root = (config.root || '/').replace(/\/?$/, '/');
  let running = false, stop = false, message = '', done = 0, total = 0;
  const locked = new Map();
  const text = (key, values = {}) => String(labels[key] || key).replace(/\{(\w+)\}/g, (_, k) => values[k] ?? '');
  function paint() {
    document.querySelectorAll('[data-bulk-cart]').forEach(panel => {
      panel.hidden = false;
      panel.querySelector('[data-bulk-add]').disabled = running;
      panel.querySelector('[data-bulk-stop]').hidden = !running;
      panel.querySelector('[data-bulk-stop]').disabled = stop;
      panel.querySelector('[data-bulk-status]').textContent = message;
      const progress = panel.querySelector('[data-bulk-progress]');
      progress.hidden = !running || !total;
      progress.max = Math.max(total, 1); progress.value = done;
    });
    if (running) lockCart();
  }
  function lockCart() {
    document.querySelectorAll('.cart-page form input,.cart-page form button,.cart-footer form button,[data-line-key],[data-remove-key]').forEach(el => {
      if (!locked.has(el)) locked.set(el, el.disabled);
      el.disabled = true;
    });
  }
  function unlockCart() {
    locked.forEach((disabled, el) => { if (el.isConnected) el.disabled = disabled; });
    locked.clear();
  }
  async function request(url, options = {}, asText = false) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal,
        headers: {Accept: asText ? 'text/html' : 'application/json', ...options.headers}});
      const data = asText ? await response.text() : await response.json();
      if (!response.ok) { const error = new Error('Store request failed'); error.status = response.status; throw error; }
      return data;
    } finally { clearTimeout(timer); }
  }
  const cart = () => request(`${root}cart.js`);
  const present = data => new Set(data.items.map(item => String(item.product_id)));
  async function catalog(base) {
    const products = new Map();
    for (let page = 1; !stop; page++) {
      const url = new URL(base, location.origin);
      url.searchParams.set('view', 'bulk-cart');
      url.searchParams.set('sort_by', 'title-ascending');
      url.searchParams.set('page', String(page));
      const source = await request(url, {}, true);
      const node = new DOMParser().parseFromString(source, 'text/html').getElementById('BulkCatalogData');
      if (!node) throw new Error('Catalog response unavailable');
      const data = JSON.parse(node.textContent);
      if (data.page !== page || !Array.isArray(data.products) || !Number.isInteger(data.pages)) throw new Error('Invalid catalog page');
      data.products.forEach(item => { if (item.productId) products.set(String(item.productId), item); });
      if (page >= data.pages) break;
    }
    return [...products.values()];
  }
  const post = items => request(`${root}cart/add.js`, {method: 'POST', headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({items: items.map(item => ({id: item.variantId, quantity: 1}))})});
  async function refresh() {
    await window.FreezzinCart.refresh();
    const page = document.querySelector('[data-cart-section]');
    if (page) {
      const url = new URL(config.cartUrl || `${root}cart`, location.origin);
      url.searchParams.set('section_id', page.dataset.cartSection);
      const html = await request(url, {}, true);
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-section]');
      if (!next) throw new Error('Cart page unavailable');
      page.replaceWith(next);
    }
  }
  async function start(button) {
    if (running) return;
    running = true; stop = false; done = 0; total = 0;
    const fromPage = !!button.closest('[data-cart-section]');
    const catalogUrl = button.closest('[data-bulk-cart]').dataset.catalogUrl;
    document.documentElement.dataset.bulkCartBusy = 'true';
    const counts = {added: 0, existing: 0, skipped: 0, failed: 0};
    let initial = new Set(), candidates = [], touched = false, error = false;
    message = text('loading'); paint();
    try {
      // Read every catalog page before any write; a failed scan never adds a partial catalog.
      const products = await catalog(catalogUrl);
      initial = present(await cart());
      for (const product of products) {
        if (initial.has(String(product.productId))) counts.existing++;
        else if (!product.variantId) counts.skipped++;
        else candidates.push(product);
      }
      total = candidates.length;
      for (let i = 0; i < candidates.length && !stop; i += 10) {
        const batch = candidates.slice(i, i + 10);
        message = text('adding', {done, total}); paint();
        touched = true;
        try {
          await post(batch);
        } catch (failure) {
          // Shopify can partially apply an unsuccessful request. Reconcile before retrying.
          const now = present(await cart());
          const missing = batch.filter(item => !now.has(String(item.productId)));
          if (failure.status !== 422) throw failure;
          for (const item of missing) {
            if (stop) break;
            try { await post([item]); }
            catch (single) {
              const current = present(await cart());
              if (!current.has(String(item.productId))) {
                if (single.status !== 422) throw single;
                counts.failed++;
              }
            }
          }
        }
        done = Math.min(i + batch.length, total);
        message = text('adding', {done, total}); paint();
      }
    } catch { error = true; }
    finally {
      if (touched) {
        try {
          const latest = await cart(), current = present(latest);
          counts.added = candidates.filter(item => !initial.has(String(item.productId)) && current.has(String(item.productId))).length;
          document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = latest.item_count; });
        } catch { error = true; }
      }
      const summary = text('done', counts);
      message = error ? text('error', {summary}) : stop ? text('stopped', {summary}) : summary;
      try { if (touched) await refresh(); }
      catch { message += ' ' + text('refresh_error'); }
      running = false;
      delete document.documentElement.dataset.bulkCartBusy;
      unlockCart(); paint();
      const scope = document.querySelector(fromPage ? '[data-cart-section]' : '#CartDrawer');
      if (fromPage || scope?.open) scope?.querySelector('[data-bulk-add]')?.focus({preventScroll: true});
    }
  }
  document.addEventListener('click', event => {
    const add = event.target.closest('[data-bulk-add]');
    if (add) { event.preventDefault(); start(add); }
    if (event.target.closest('[data-bulk-stop]')) { event.preventDefault(); stop = true; paint(); }
    if (running && event.target.closest('.cart-page a[href*="/cart/change"]')) event.preventDefault();
  });
  document.addEventListener('submit', event => {
    if (running && event.target.closest('.cart-page form,.cart-footer form')) event.preventDefault();
  }, true);
  document.addEventListener('cart:rendered', paint);
  document.addEventListener('shopify:section:load', paint);
  paint();
})();
